import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Result, Skeleton, Typography } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useGlobal } from "../../contexts/GlobalContext";
import { projectPath } from "../../config/nav";
import request from "../../utils/request";
import { overviewTone, percent, siteOverviewPath, type SiteOverviewDTO } from "./siteOverviewModel";
import "./SiteOverview.css";

const { Title, Text } = Typography;

const statusCards: Array<[keyof Pick<SiteOverviewDTO, "connection" | "publish" | "knowledge" | "quota">, string, string]> = [
  ["connection", "连接", "ri-link"], ["publish", "发布", "ri-rocket-line"],
  ["knowledge", "知识", "ri-book-open-line"], ["quota", "额度", "ri-gauge-line"],
];

const SiteOverview: React.FC = () => {
  const { siteId = "" } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { API_BASE_URL, projects } = useGlobal();
  const [overview, setOverview] = useState<SiteOverviewDTO>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError("");
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(siteId)}/overview`, { signal });
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "网站概览读取失败");
      setOverview(response.data.data);
    } catch (reason: any) {
      if (reason?.code !== "ERR_CANCELED") setError(reason.response?.data?.msg || reason.message || "网站概览读取失败");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [API_BASE_URL, siteId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const legacyProject = useMemo(
    () => projects.find((project) => project.id === overview?.site.legacyProjectId),
    [overview?.site.legacyProjectId, projects]
  );

  if (loading && !overview) return <main className="site-overview"><Skeleton active paragraph={{ rows: 12 }} /></main>;
  if (error || !overview) return <main className="site-overview"><Result status="error" title="无法读取网站概览" subTitle={error} extra={<Button onClick={() => void load()}>重试</Button>} /></main>;

  const openAdvice = () => navigate(siteOverviewPath(siteId, overview.advice.path));
  return (
    <main className="site-overview" aria-labelledby="site-overview-title">
      <header className="site-overview__heading">
        <div>
          <Text type="secondary">网站概览</Text>
          <Title id="site-overview-title" level={2}>{overview.site.name}</Title>
          <span>{overview.site.primaryUrl || "尚未设置网站 URL"}</span>
        </div>
        <div className="site-overview__heading-actions">
          {legacyProject ? <Button onClick={() => navigate(projectPath(legacyProject.key_value, "overview"))}>打开旧项目视图</Button> : null}
          <Button type="primary" icon={<i className="ri-test-tube-line" />} onClick={() => navigate(siteOverviewPath(siteId, "test"))}>测试网站</Button>
        </div>
      </header>

      <section className="overview-status-grid" aria-label="网站状态">
        {statusCards.map(([key, title, icon]) => {
          const item = overview[key];
          const tone = overviewTone(item.status);
          return <article key={key} className={`overview-status overview-status--${tone}`}><i className={icon} /><span><small>{title}</small><strong>{item.label}</strong><em>{item.detail}</em></span></article>;
        })}
      </section>

      <section className="overview-advice" aria-labelledby="overview-advice-title">
        <div className="overview-advice__icon"><i className="ri-sparkling-2-fill" /></div>
        <div>
          <Text type="secondary">当前唯一推荐</Text>
          <Title id="overview-advice-title" level={3}>{overview.advice.title}</Title>
          <dl><div><dt>依据</dt><dd>{overview.advice.evidence}</dd></div><div><dt>影响</dt><dd>{overview.advice.impact}</dd></div></dl>
        </div>
        <Button type="primary" size="large" onClick={openAdvice}>{overview.advice.action}<i className="ri-arrow-right-line" /></Button>
      </section>

      <section className="overview-content-grid">
        <div className="overview-metrics">
          <header><div><Text type="secondary">运行效果</Text><Title level={4}>今日与近 7 日</Title></div><Button type="link" onClick={() => navigate(siteOverviewPath(siteId, "analytics"))}>查看分析</Button></header>
          <div className="overview-metrics__table">
            <span></span><strong>今日</strong><strong>近 7 日</strong>
            <span>任务数</span><b>{overview.today.tasks}</b><b>{overview.sevenDays.tasks}</b>
            <span>成功</span><b>{overview.today.succeeded}</b><b>{overview.sevenDays.succeeded}</b>
            <span>失败 / 未知</span><b>{overview.today.failed}</b><b>{overview.sevenDays.failed}</b>
            <span>成功率</span><b>{percent(overview.today.successRate)}</b><b>{percent(overview.sevenDays.successRate)}</b>
          </div>
        </div>
        <div className="overview-failures">
          <header><div><Text type="secondary">需要关注</Text><Title level={4}>最近失败</Title></div><Button type="link" onClick={() => navigate(siteOverviewPath(siteId, "test"))}>进入测试</Button></header>
          {overview.recentFailures.length === 0 ? <div className="overview-empty"><i className="ri-checkbox-circle-line" /><strong>暂无失败任务</strong><span>发生失败后会在这里显示脱敏原因与 Trace 入口。</span></div> : (
            <ul>{overview.recentFailures.map((failure) => <li key={failure.executionId}><i className="ri-error-warning-line" /><span><strong>{failure.intent}</strong><small>{failure.errorCode || "unknown_error"} · {new Date(failure.occurredAt).toLocaleString()}</small></span><Button type="link" onClick={() => navigate(siteOverviewPath(siteId, `test?execution=${encodeURIComponent(failure.executionId)}`))}>查看</Button></li>)}</ul>
          )}
        </div>
      </section>
    </main>
  );
};

export default SiteOverview;
